import { useCallback, useEffect, useRef, useState } from 'react'

const OPEN_DELAY_MS = 70
const CLOSE_DELAY_MS = 200

type PointerHandler = (event: React.PointerEvent<HTMLElement>) => void

export function useHoverMenu() {
  const [isOpen, setIsOpen] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isPointerDrivenRef = useRef(false)

  const cancelPending = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = null
  }, [])

  useEffect(() => cancelPending, [cancelPending])

  const handlePointerEnter = useCallback<PointerHandler>(
    (event) => {
      if (event.pointerType !== 'mouse') return

      cancelPending()
      timerRef.current = setTimeout(() => {
        isPointerDrivenRef.current = true
        setIsOpen(true)
      }, OPEN_DELAY_MS)
    },
    [cancelPending],
  )

  const handlePointerLeave = useCallback<PointerHandler>(
    (event) => {
      if (event.pointerType !== 'mouse') return

      cancelPending()
      timerRef.current = setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS)
    },
    [cancelPending],
  )

  // `preventDefault` stops Radix's own toggle, so this has to toggle itself.
  const handlePointerDown = useCallback<PointerHandler>(
    (event) => {
      if (event.pointerType !== 'mouse') return

      event.preventDefault()
      cancelPending()
      isPointerDrivenRef.current = true
      setIsOpen((wasOpen) => !wasOpen)
    },
    [cancelPending],
  )

  const handleOpenChange = useCallback(
    (open: boolean) => {
      cancelPending()
      if (open) isPointerDrivenRef.current = false
      setIsOpen(open)
    },
    [cancelPending],
  )

  // Hovering must not steal focus from whatever the operator is typing in; keyboard opens still do.
  const handleAutoFocus = useCallback((event: Event) => {
    if (isPointerDrivenRef.current) event.preventDefault()
  }, [])

  return {
    rootProps: { open: isOpen, onOpenChange: handleOpenChange, modal: false as const },
    triggerProps: {
      onPointerEnter: handlePointerEnter,
      onPointerLeave: handlePointerLeave,
      onPointerDown: handlePointerDown,
    },
    contentProps: {
      onPointerEnter: handlePointerEnter,
      onPointerLeave: handlePointerLeave,
      onOpenAutoFocus: handleAutoFocus,
      onCloseAutoFocus: handleAutoFocus,
    },
  }
}
